import { Button, Icon, Input } from "@legal-500/legal500-design-system";
import { useEffect } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import type { AddressGridAddress, AddressGridContent } from "./types";
import styles from "../CMS.module.css";

type AddressGridEditorProps = {
  value: AddressGridContent;
  onChange: (value: AddressGridContent) => void;
};

type AddressGridFormAddress = {
  city: string;
  linesText: string;
  order: number | undefined;
};

type AddressGridFormValues = {
  addresses: AddressGridFormAddress[];
};

function toFormAddress(address: AddressGridAddress): AddressGridFormAddress {
  return {
    city: address.city ?? "",
    linesText: (address.lines ?? []).join("\n"),
    order: address.order ?? 0,
  };
}

function getNextOrder(addresses: AddressGridFormAddress[]): number {
  const orders = addresses
    .map(address => address.order)
    .filter((order): order is number => typeof order === "number" && !Number.isNaN(order));
  if (orders.length === 0) {
    return 0;
  }
  return Math.max(...orders) + 1;
}

function toLines(linesText: string | undefined): string[] {
  return (linesText ?? "")
    .split("\n")
    .map(line => line.trim())
    .filter(line => line.length > 0);
}

function toAddress(formAddress: Partial<AddressGridFormAddress> | undefined): AddressGridAddress {
  const order = formAddress?.order;
  return {
    city: (formAddress?.city ?? "").trim(),
    lines: toLines(formAddress?.linesText),
    order: typeof order === "number" && !Number.isNaN(order) ? order : undefined,
  };
}

export function AddressGridEditor({ value, onChange }: AddressGridEditorProps) {
  const methods = useForm<AddressGridFormValues>({
    defaultValues: { addresses: (value.addresses ?? []).map(toFormAddress) },
  });

  const { control, register, watch } = methods;

  const { fields, append, remove } = useFieldArray<AddressGridFormValues>({
    control,
    name: "addresses",
  });

  useEffect(() => {
    const subscription = watch(formValues => {
      if (formValues.addresses) {
        onChange({ addresses: formValues.addresses.map(toAddress) });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  function handleAddAddress() {
    append({ city: "", linesText: "", order: getNextOrder(watch("addresses")) });
  }

  return (
    <FormProvider {...methods}>
      <div className={styles.cardEditorList}>
        {fields.map((field, index) => (
          <div key={field.id} className={styles.cardEditorItem}>
            <div className={styles.cardEditorItemHeader}>
              <span className={styles.cardEditorItemTitle}>Office {index + 1}</span>
              <div className={styles.cardEditorItemActions}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => remove(index)}
                  aria-label={`Delete office ${index + 1}`}
                >
                  <Icon icon="delete" size="small" />
                </Button>
              </div>
            </div>

            <div className={styles.cardEditorFields}>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`address-grid-city-${field.id}`}>
                  City <span className={styles.required}>*</span>
                </label>
                <Input
                  id={`address-grid-city-${field.id}`}
                  required
                  pattern=".*\S.*"
                  title="Enter a city name"
                  {...register(`addresses.${index}.city`)}
                />
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`address-grid-lines-${field.id}`}>
                  Address lines (one per line)
                </label>
                <textarea
                  id={`address-grid-lines-${field.id}`}
                  className={styles.textarea}
                  rows={4}
                  {...register(`addresses.${index}.linesText`)}
                />
              </div>

              <div className={styles.cardEditorFieldRow}>
                <div className={styles.formField}>
                  <label className={styles.formLabel} htmlFor={`address-grid-order-${field.id}`}>
                    Order <span className={styles.required}>*</span>
                  </label>
                  <Input
                    id={`address-grid-order-${field.id}`}
                    type="number"
                    required
                    {...register(`addresses.${index}.order`, { valueAsNumber: true })}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        <Button variant="outlined" color="primary" onClick={handleAddAddress}>
          <Icon icon="plus" size="small" />
          Add Office
        </Button>
      </div>
    </FormProvider>
  );
}
